import { useState } from 'react';
import { websiteAllowedLinkKinds, type SupabaseClient } from '@dukanti/shared';
import { DestinationEditor } from '@/components/destination-editor';
import { useDestinationAvailability } from '../../hooks/use-destination-availability';
import { useWebsiteT } from '../../hooks/use-website-t';
import { destinationIssueKey } from '../../lib/issue-messages';
import { slotFieldLabel } from '../../lib/region-labels';
import { currentLink } from '../../lib/slot-values';
import type { SlotFieldProps } from './slot-text-field';

export interface SlotDestinationFieldProps extends SlotFieldProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly templateId: string;
  readonly supabaseUrl: string;
  /**
   * The element's button label resolves empty: on the storefront nothing is
   * tappable, so the destination would do nothing (epic #228 P5, DA major 3).
   */
  readonly labelMissing?: boolean;
  /** The merchant tried to close the dialog with this choice unfinished (UI/UX P2-2). */
  readonly finishRequested?: boolean;
}

/**
 * Epic #228 P5: a link slot of a region, on the shared DestinationEditor.
 * It offers the kinds the slot allows (`websiteAllowedLinkKinds`: the
 * manifest's targets plus Instagram/Facebook unless the slot opts out).
 * Every choice goes through `onChange` → `editor.setField`, which checks it
 * (RULE 0) before the auto-save queue sends it; "No link" is an authored
 * `{ kind: 'none' }`, never the template default.
 */
export function SlotDestinationField({
  slot,
  state,
  onChange,
  issueKey,
  client,
  storeId,
  templateId,
  supabaseUrl,
  labelMissing = false,
  finishRequested = false,
}: SlotDestinationFieldProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const link = currentLink(slot, state);
  const availability = useDestinationAvailability({
    client,
    storeId,
    templateId,
    slotId: slot.id,
    link,
  });
  // Readiness speaks about what the dialog opened with; a new pick is not "unavailable"
  // while the readiness refetch after its save is on its way (PD10, AC6).
  const [openedTarget] = useState(() => targetKey(link));
  const unavailable = availability.unavailable && targetKey(link) === openedTarget;

  return (
    <div data-testid={`field-${slot.id}`}>
      <DestinationEditor
        client={client}
        supabaseUrl={supabaseUrl}
        storeId={storeId}
        value={link}
        allowedKinds={websiteAllowedLinkKinds(slot)}
        unavailable={unavailable}
        selectedLabel={availability.selectedLabel}
        issueKey={destinationIssueKey(issueKey)}
        label={slotFieldLabel(t, slot)}
        testIdPrefix={`destination-${slot.id}`}
        noticeKey={labelMissing ? 'admin.destination.labelMissing' : undefined}
        finishRequested={finishRequested}
        onChange={(next) => onChange({ mode: 'authored', value: { v: next } })}
      />
    </div>
  );
}

function targetKey(link: ReturnType<typeof currentLink>): string {
  if (link.kind === 'category') return `category:${link.categoryId}`;
  if (link.kind === 'product') return `product:${link.productId}`;
  if (link.kind === 'custom-collection') return `collection:${link.collectionRef}`;
  return link.kind;
}
